import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';

import { untilViewDestroyed } from '@sd-portal/shared/rxjs';

@Component({
  selector: 'sd-validation-messages',
  templateUrl: './validation-messages.component.html',
  styleUrls: ['./validation-messages.component.scss']
})
export class ValidationMessagesComponent implements OnInit, OnDestroy {
  /* component properties */
  messages: string[];

  @Input() control: AbstractControl;
  @Input() debounceMs: number = 300;
  @Input() showFirstOnly: boolean = true;
  @Input() updateOnTouch: boolean = false;
  @Output() controlEvent = new EventEmitter();

  /* privates */
  private updateValidationMessage() {
    // reset validation messages
    this.messages = [];
    // get control validation objects
    let e: {
      min?: number;
      max?: number;
      requiredLength?: number;
    } = {};
    const errors = (this.control.errors as ValidationErrors) || [];

    for (const err in errors) {
      if (errors.hasOwnProperty(err)) {
        let msg = '';
        switch (err) {
          case 'required':
            this.messages.push('Required');
            break;
          case 'min':
            e = errors[err];
            this.messages.push(`Must be ${e['min']} or greater`);
            break;
          case 'max':
            e = errors[err];
            this.messages.push(`Must be ${e['max']} or less`);
            break;
          case 'minlength':
            e = errors[err];
            this.messages.push(`Must be at least ${e['requiredLength']} characters`);
            break;
          case 'maxlength':
            e = errors[err];
            this.messages.push(`Must be ${e['requiredLength']} characters or less`);
            break;
          case 'minDate':
          case 'matDatepickerMin':
            e = errors[err];
            const { min } = e;
            const emin = moment.isMoment(min) ? min : null;
            msg = emin
              ? `Selected date must be after ${emin.format('M/D/YY')}`
              : 'Selected date precedes minimum';
            this.messages.push(msg);
            break;
          case 'maxDate':
          case 'matDatepickerMax':
            e = errors[err];
            const { max } = e;
            const emax = moment.isMoment(max) ? max : null;
            msg = emax
              ? `Selected date must be before ${emax.format('M/D/YY')}`
              : 'Selected date exceeds maximum';
            this.messages.push(msg);
            break;
          case 'pattern':
            e = errors[err];
            this.messages.push('Invalid format');
            break;
          case 'email':
            e = errors[err];
            this.messages.push('Invalid email');
            break;
          case 'matDateParse':
          case 'date':
            e = errors[err];
            this.messages.push('Invalid date');
            break;
          case 'phone':
            e = errors[err];
            this.messages.push('Invalid phone number');
            break;
          case 'invalidFormat':
            e = errors[err];
            this.messages.push('Invalid value / format');
            break;
          case 'invalidDaterange':
            e = errors[err];
            this.messages.push('End date must not precede start date');
            break;
          case 'uniqueRequired':
            e = errors[err];
            this.messages.push('Unique value required');
            break;
          case 'typeAheadObjectNotSelected':
            e = errors[err];
            this.messages.push('Value not selected');
            break;
          case 'endDateBeforeStart':
            e = errors[err];
            this.messages.push('Date/Time before start');
            break;
          case 'customMessage':
            this.messages.push(errors[err]);
            break;
          case 'invalidCrewType':
            e = errors[err];
            this.messages.push('Crew member is not rated for selected aircraft');
            break;
          case 'Validrange999':
            this.messages.push('Valid range 0-999');
            break;
          case 'Validrange255':
            this.messages.push('Valid range 0-255');
            break;
        }

        if (this.showFirstOnly) {
          break;
        }
      }
    }
  }

  /* life-cycle */
  ngOnInit() {
    if (this.control) {
      // init validation message
      this.updateValidationMessage();

      // update validation message on changes
      this.control.valueChanges
        .pipe(untilViewDestroyed(this), debounceTime(this.debounceMs))
        .subscribe(_changes => {
          this.updateValidationMessage();
        });

      if (this.updateOnTouch) {
        this.control.statusChanges
          .pipe(untilViewDestroyed(this), debounceTime(this.debounceMs))
          .subscribe(() => {
            this.updateValidationMessage();
          });
      }
    }
  }

  ngOnDestroy() {
    // for ngUnsubscribe AOT
  }
}
